import React from 'react';
import { useAuth } from '../../auth/AuthContext';
import { hasAccountingAccess } from '../../../subscription';
import {
    allocateBankTransaction,
    deleteBankTransaction,
    allocatePaymentProviderTransaction,
    deletePaymentProviderTransaction,
    getCustomers,
    getExpense,
    getIncome,
    getOpenInvoices,
    getSuppliers,
    getPendingBankTransactions,
    getUnassignedPaymentProviderTransactions,
    createExpense,
    createSupplier,
    createIncome,
    createCustomer,
} from '../../../api';
import {
    buildAllocationRow,
    buildCustomerPayload,
    buildExpensePayload,
    buildIncomePayload,
    buildSupplierPayload,
    parseAmount,
    resolveReferenceList,
    toCurrencyString,
    toDateInputValue,
} from './unassignedTransactionsUtils';
import {
    buildCustomerDisplayName,
    buildCustomerOptions,
} from '../../shared/customerSelectUtils';
import {
    buildSupplierDisplayName,
    buildSupplierOptions,
} from '../../shared/supplierSelectUtils';

export default function useUnassignedTransactions() {
    const { user } = useAuth();
    const hasAccounting = hasAccountingAccess(user?.paymentPlan);

    const [transactions, setTransactions] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [bankTransactions, setBankTransactions] = React.useState([]);
    const [bankLoading, setBankLoading] = React.useState(true);
    const [bankError, setBankError] = React.useState('');
    const [bankAssignDialogOpen, setBankAssignDialogOpen] = React.useState(false);
    const [activeBankTransaction, setActiveBankTransaction] = React.useState(null);
    const [bankAssigning, setBankAssigning] = React.useState(false);
    const [bankAssignError, setBankAssignError] = React.useState('');
    const [bankAllocationRows, setBankAllocationRows] = React.useState([]);
    const [bankAllocationErrors, setBankAllocationErrors] = React.useState({});
    const [bankCreatingCustomer, setBankCreatingCustomer] = React.useState(false);
    const [bankCreatingSupplier, setBankCreatingSupplier] = React.useState(false);
    const [bankComment, setBankComment] = React.useState('');

    const [assignDialogOpen, setAssignDialogOpen] = React.useState(false);
    const [activeTransaction, setActiveTransaction] = React.useState(null);
    const [allocationRows, setAllocationRows] = React.useState([]);
    const [assigning, setAssigning] = React.useState(false);
    const [deletingTransactionId, setDeletingTransactionId] = React.useState(null);
    const [deletingBankTransactionId, setDeletingBankTransactionId] = React.useState(null);
    const [assignError, setAssignError] = React.useState('');
    const [allocationErrors, setAllocationErrors] = React.useState({});

    const [referenceLoading, setReferenceLoading] = React.useState(false);
    const [referenceData, setReferenceData] = React.useState({
        invoices: [],
        incomes: [],
        expenses: [],
        customers: [],
        suppliers: [],
    });
    const [creatingCustomer, setCreatingCustomer] = React.useState(false);
    const [creatingSupplier, setCreatingSupplier] = React.useState(false);

    const fetchTransactions = React.useCallback(async () => {
        if (!hasAccounting) {
            setTransactions([]);
            setBankTransactions([]);
            setLoading(false);
            setBankLoading(false);
            return;
        }
        setLoading(true);
        setBankLoading(true);
        setError('');
        setBankError('');
        try {
            const res = await getUnassignedPaymentProviderTransactions();
            setTransactions(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            setError('Vorgänge konnten nicht geladen werden.');
        } finally {
            setLoading(false);
        }
        try {
            const res = await getPendingBankTransactions();
            setBankTransactions(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            setBankError('Bankvorgänge konnten nicht geladen werden.');
        } finally {
            setBankLoading(false);
        }
    }, [hasAccounting]);

    React.useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    React.useEffect(() => {
        if (!bankAssignDialogOpen || referenceData.invoices.length === 0) {
            return;
        }
        setBankAllocationRows((prev) => {
            if (prev.length === 0) {
                return prev;
            }
            const row = prev[0];
            if (row.allocationType !== 'INVOICE' || row.targetId) {
                return prev;
            }
            const defaultInvoiceId = activeBankTransaction?.matchedInvoiceId
                || referenceData.invoices[0]?.id
                || '';
            if (!defaultInvoiceId) {
                return prev;
            }
            return [
                {
                    ...row,
                    targetId: defaultInvoiceId,
                },
                ...prev.slice(1),
            ];
        });
    }, [referenceData.invoices, bankAssignDialogOpen, activeBankTransaction]);

    const loadReferenceData = React.useCallback(async (bookingDate) => {
        if (referenceLoading) {
            return;
        }
        setReferenceLoading(true);
        const params = {};
        if (bookingDate) {
            try {
                const date = new Date(bookingDate);
                const fromDate = new Date(date.getFullYear() - 1, date.getMonth(), date.getDate());
                const toDate = new Date(date.getFullYear() + 1, date.getMonth(), date.getDate());
                const from = fromDate.toISOString().slice(0, 10);
                const to = toDate.toISOString().slice(0, 10);
                params.dueFrom = from;
                params.dueTo = to;
                params.from = from;
                params.to = to;
            } catch (e) {
                // ignore invalid date
            }
        }
        try {
            const results = await Promise.allSettled([
                getOpenInvoices(params),
                getIncome(params),
                getExpense(params),
                getCustomers(),
                getSuppliers(),
            ]);
            const [invoiceRes, incomeRes, expenseRes, customerRes, supplierRes] = results;
            setReferenceData({
                invoices: resolveReferenceList(invoiceRes),
                incomes: resolveReferenceList(incomeRes),
                expenses: resolveReferenceList(expenseRes),
                customers: resolveReferenceList(customerRes),
                suppliers: resolveReferenceList(supplierRes),
            });
        } catch (err) {
            setReferenceData({
                invoices: [],
                incomes: [],
                expenses: [],
                customers: [],
                suppliers: [],
            });
        } finally {
            setReferenceLoading(false);
        }
    }, [referenceLoading]);

    const openAssignDialog = (transaction) => {
        if (!transaction) {
            return;
        }
        const transactionDate = toDateInputValue(transaction.transactionDateTime);
        const defaultAmount = toCurrencyString(Math.abs(Number(transaction.remainingAmount ?? transaction.netAmount ?? 0)));
        const providerId = transaction.providerTransactionId || '';
        setActiveTransaction(transaction);
        setAssignError('');
        setCreatingCustomer(false);
        setCreatingSupplier(false);
        setAllocationErrors({});
        setAllocationRows([
            buildAllocationRow({
                allocationType: transaction.netAmount != null && Number(transaction.netAmount) < 0 ? 'EXPENSE' : 'INVOICE',
                amount: defaultAmount,
                paymentDate: transactionDate,
                newIncome: {
                    invoiceDate: transactionDate,
                    invoiceNumber: providerId,
                    customerSearch: transaction.name || '',
                    grossAmount: defaultAmount,
                    netAmount: defaultAmount,
                    vatAmount: '0',
                },
                newExpense: {
                    invoiceDate: transactionDate,
                    invoiceNumber: providerId,
                    supplierName: transaction.name || '',
                    grossAmount: defaultAmount,
                    netAmount: defaultAmount,
                    vatAmount: '0',
                },
            }),
        ]);
        setAssignDialogOpen(true);
        loadReferenceData(transaction.transactionDateTime);
    };

    const openBankAssignDialog = (transaction) => {
        if (!transaction) {
            return;
        }
        setActiveBankTransaction(transaction);
        const bookingDate = toDateInputValue(transaction.bookingDate || transaction.valueDate);
        const defaultAmount = toCurrencyString(Math.abs(Number(transaction.amount ?? 0)));
        const isExpense = Number(transaction.amount ?? 0) < 0;
        const defaultType = isExpense ? 'EXPENSE' : 'INVOICE';
        const defaultInvoiceId = transaction.matchedInvoiceId || referenceData.invoices?.[0]?.id || '';
        setBankAllocationRows([
            buildAllocationRow({
                allocationType: defaultType,
                targetId: defaultType === 'INVOICE' ? defaultInvoiceId : '',
                amount: defaultAmount,
                paymentDate: bookingDate,
                newIncome: {
                    invoiceDate: bookingDate,
                    invoiceNumber: transaction.externalId || '',
                    customerSearch: transaction.counterpartName || '',
                    grossAmount: defaultAmount,
                    netAmount: defaultAmount,
                    vatAmount: '0',
                },
                newExpense: {
                    invoiceDate: bookingDate,
                    invoiceNumber: transaction.externalId || '',
                    supplierName: transaction.counterpartName || '',
                    grossAmount: defaultAmount,
                    netAmount: defaultAmount,
                    vatAmount: '0',
                },
            }),
        ]);
        setBankComment('');
        setBankAssignError('');
        setBankCreatingCustomer(false);
        setBankCreatingSupplier(false);
        setBankAllocationErrors({});
        setBankAssignDialogOpen(true);
        loadReferenceData(transaction.bookingDate || transaction.valueDate);
    };

    const closeAssignDialog = () => {
        setAssignDialogOpen(false);
        setActiveTransaction(null);
        setAllocationRows([]);
        setAssignError('');
        setCreatingCustomer(false);
        setCreatingSupplier(false);
        setAllocationErrors({});
    };

    const closeBankAssignDialog = () => {
        setBankAssignDialogOpen(false);
        setActiveBankTransaction(null);
        setBankAssignError('');
        setBankAllocationRows([]);
        setBankCreatingCustomer(false);
        setBankCreatingSupplier(false);
        setBankComment('');
        setBankAllocationErrors({});
    };

    const clearRowError = (setter, rowId, field) => {
        setter((prev) => {
            const rowErrors = prev?.[rowId];
            if (!rowErrors || !rowErrors[field]) {
                return prev;
            }
            const nextRow = { ...rowErrors };
            delete nextRow[field];
            const next = { ...prev };
            if (Object.keys(nextRow).length > 0) {
                next[rowId] = nextRow;
            } else {
                delete next[rowId];
            }
            return next;
        });
    };

    const setRowError = (setter, rowId, field, message) => {
        setter((prev) => ({
            ...(prev || {}),
            [rowId]: {
                ...(prev?.[rowId] || {}),
                [field]: message,
            },
        }));
    };

    const clearAllocationError = (rowId, field) => clearRowError(setAllocationErrors, rowId, field);
    const clearBankAllocationError = (rowId, field) => clearRowError(setBankAllocationErrors, rowId, field);
    const setAllocationRowError = (rowId, field, message) =>
        setRowError(setAllocationErrors, rowId, field, message);
    const setBankAllocationRowError = (rowId, field, message) =>
        setRowError(setBankAllocationErrors, rowId, field, message);

    const updateAllocationRow = (id, patch) => {
        Object.keys(patch || {}).forEach((field) => clearAllocationError(id, field));
        setAllocationRows((prev) =>
            prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
        );
    };

    const updateAllocationNested = (id, key, patch) => {
        Object.keys(patch || {}).forEach((field) => clearAllocationError(id, field));
        setAllocationRows((prev) =>
            prev.map((row) => {
                if (row.id !== id) {
                    return row;
                }
                return {
                    ...row,
                    [key]: {
                        ...row[key],
                        ...patch,
                    },
                };
            })
        );
    };

    const updateBankAllocationRow = (id, patch) => {
        Object.keys(patch || {}).forEach((field) => clearBankAllocationError(id, field));
        setBankAllocationRows((prev) =>
            prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
        );
    };

    const updateBankAllocationNested = (id, key, patch) => {
        Object.keys(patch || {}).forEach((field) => clearBankAllocationError(id, field));
        setBankAllocationRows((prev) =>
            prev.map((row) => {
                if (row.id !== id) {
                    return row;
                }
                return {
                    ...row,
                    [key]: {
                        ...row[key],
                        ...patch,
                    },
                };
            })
        );
    };

    const addAllocationRow = () => {
        if (!activeTransaction) {
            return;
        }
        setAllocationRows((prev) => [
            ...prev,
            buildAllocationRow({
                allocationType: 'INVOICE',
                paymentDate: toDateInputValue(activeTransaction.transactionDateTime),
            }),
        ]);
    };

    const removeAllocationRow = (id) => {
        setAllocationRows((prev) => prev.filter((row) => row.id !== id));
        setAllocationErrors((prev) => {
            if (!prev?.[id]) return prev;
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    const addBankAllocationRow = () => {
        if (!activeBankTransaction) {
            return;
        }
        setBankAllocationRows((prev) => [
            ...prev,
            buildAllocationRow({
                allocationType: 'INVOICE',
                paymentDate: toDateInputValue(activeBankTransaction.bookingDate || activeBankTransaction.valueDate),
            }),
        ]);
    };

    const removeBankAllocationRow = (id) => {
        setBankAllocationRows((prev) => prev.filter((row) => row.id !== id));
        setBankAllocationErrors((prev) => {
            if (!prev?.[id]) return prev;
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    const handleAllocationTypeChange = (id, allocationType) => {
        updateAllocationRow(id, {
            allocationType,
            targetId: allocationType === 'INVOICE' || allocationType === 'INCOME' || allocationType === 'EXPENSE' ? '' : '',
        });
    };

    const handleBankAllocationTypeChange = (id, allocationType) => {
        updateBankAllocationRow(id, {
            allocationType,
            targetId: allocationType === 'INVOICE' || allocationType === 'INCOME' || allocationType === 'EXPENSE' ? '' : '',
        });
    };

    const handleCustomerSelection = (id, value, updateNested = updateAllocationNested) => {
        if (!value) {
            updateNested(id, 'newIncome', {
                customerId: '',
                customerName: '',
                customerSearch: '',
            });
            return;
        }
        updateNested(id, 'newIncome', {
            customerId: value.id,
            customerName: value.label || buildCustomerDisplayName(value),
            customerSearch: value.label || buildCustomerDisplayName(value),
        });
    };

    const handleCustomerInput = (id, value, reason, updateNested = updateAllocationNested) => {
        if (reason === 'input') {
            updateNested(id, 'newIncome', {
                customerSearch: value,
                customerName: value,
                customerId: '',
            });
        }
    };

    const handleSupplierSelection = (id, value, updateNested = updateAllocationNested) => {
        if (!value) {
            updateNested(id, 'newExpense', {
                supplierId: '',
                supplierName: '',
            });
            return;
        }
        updateNested(id, 'newExpense', {
            supplierId: value.id,
            supplierName: value.label || buildSupplierDisplayName(value),
        });
    };

    const handleSupplierInput = (id, value, reason, updateNested = updateAllocationNested) => {
        if (reason === 'input') {
            updateNested(id, 'newExpense', {
                supplierName: value,
                supplierId: '',
            });
        }
    };

    const handleCreateCustomer = async (id) => {
        const row = allocationRows.find((entry) => entry.id === id);
        if (!row) return;
        const name = row.newIncome.customerSearch || row.newIncome.customerName;
        if (!name) {
            setAllocationRowError(id, 'customerName', 'Bitte einen Namen angeben.');
            return;
        }
        setCreatingCustomer(true);
        try {
            const res = await createCustomer(buildCustomerPayload(name));
            const newOption = buildCustomerDisplayName(res?.data);
            setReferenceData((prev) => ({
                ...prev,
                customers: [...prev.customers, res?.data],
            }));
            updateAllocationNested(id, 'newIncome', {
                customerId: res?.data?.id || '',
                customerName: newOption,
                customerSearch: newOption,
            });
        } catch (err) {
            setAllocationRowError(id, 'customerName', 'Kunde konnte nicht angelegt werden.');
        } finally {
            setCreatingCustomer(false);
        }
    };

    const handleCreateSupplier = async (id) => {
        const row = allocationRows.find((entry) => entry.id === id);
        if (!row) return;
        const name = row.newExpense.supplierName;
        if (!name) {
            setAllocationRowError(id, 'supplierName', 'Bitte einen Namen angeben.');
            return;
        }
        setCreatingSupplier(true);
        try {
            const res = await createSupplier(buildSupplierPayload(name));
            const newOption = buildSupplierDisplayName(res?.data);
            setReferenceData((prev) => ({
                ...prev,
                suppliers: [...prev.suppliers, res?.data],
            }));
            updateAllocationNested(id, 'newExpense', {
                supplierId: res?.data?.id || '',
                supplierName: newOption,
            });
        } catch (err) {
            setAllocationRowError(id, 'supplierName', 'Lieferant konnte nicht angelegt werden.');
        } finally {
            setCreatingSupplier(false);
        }
    };

    const handleBankCreateCustomer = async (id) => {
        const row = bankAllocationRows.find((entry) => entry.id === id);
        if (!row) return;
        const name = row.newIncome.customerSearch || row.newIncome.customerName;
        if (!name) {
            setBankAllocationRowError(id, 'customerName', 'Bitte einen Namen angeben.');
            return;
        }
        setBankCreatingCustomer(true);
        try {
            const res = await createCustomer(buildCustomerPayload(name));
            const newOption = buildCustomerDisplayName(res?.data);
            setReferenceData((prev) => ({
                ...prev,
                customers: [...prev.customers, res?.data],
            }));
            updateBankAllocationNested(id, 'newIncome', {
                customerId: res?.data?.id || '',
                customerName: newOption,
                customerSearch: newOption,
            });
        } catch (err) {
            setBankAllocationRowError(id, 'customerName', 'Kunde konnte nicht angelegt werden.');
        } finally {
            setBankCreatingCustomer(false);
        }
    };

    const handleBankCreateSupplier = async (id) => {
        const row = bankAllocationRows.find((entry) => entry.id === id);
        if (!row) return;
        const name = row.newExpense.supplierName;
        if (!name) {
            setBankAllocationRowError(id, 'supplierName', 'Bitte einen Namen angeben.');
            return;
        }
        setBankCreatingSupplier(true);
        try {
            const res = await createSupplier(buildSupplierPayload(name));
            const newOption = buildSupplierDisplayName(res?.data);
            setReferenceData((prev) => ({
                ...prev,
                suppliers: [...prev.suppliers, res?.data],
            }));
            updateBankAllocationNested(id, 'newExpense', {
                supplierId: res?.data?.id || '',
                supplierName: newOption,
            });
        } catch (err) {
            setBankAllocationRowError(id, 'supplierName', 'Lieferant konnte nicht angelegt werden.');
        } finally {
            setBankCreatingSupplier(false);
        }
    };

    const handleAssign = async () => {
        if (!activeTransaction) return;
        setAssignError('');
        setAssigning(true);
        try {
            const prepared = [];
            for (const row of allocationRows) {
                if (!row.allocationType) {
                    setAllocationRowError(row.id, 'allocationType', 'Bitte Ziel auswählen.');
                    throw new Error('Bitte Ziel fuer jede Zuordnung auswählen.');
                }
                const amount = parseAmount(row.amount);
                if (amount == null) {
                    setAllocationRowError(row.id, 'amount', 'Bitte Betrag eingeben.');
                    throw new Error('Bitte Betrag fuer jede Zuordnung eingeben.');
                }

                let targetId = row.targetId;
                let allocationType = row.allocationType;
                const transactionDate = row.paymentDate || toDateInputValue(activeTransaction.transactionDateTime);
                const currency = activeTransaction.currency || 'EUR';

                if (allocationType === 'NEW_INCOME') {
                    const payload = buildIncomePayload(row.newIncome, activeTransaction, amount, transactionDate);
                    if (!payload.customerName) {
                        setAllocationRowError(row.id, 'customerName', 'Bitte Kunde auswählen.');
                        throw new Error('Bitte Kunde auswaehlen.');
                    }
                    const createRes = await createIncome(payload);
                    targetId = createRes?.data?.id;
                    allocationType = 'INCOME';
                }

                if (allocationType === 'NEW_EXPENSE') {
                    const payload = buildExpensePayload(row.newExpense, activeTransaction, amount, transactionDate);
                    if (!payload.supplierName) {
                        setAllocationRowError(row.id, 'supplierName', 'Bitte Lieferant auswählen.');
                        throw new Error('Bitte Lieferant auswaehlen.');
                    }
                    const createRes = await createExpense(payload);
                    targetId = createRes?.data?.id;
                    allocationType = 'EXPENSE';
                }

                if (!targetId) {
                    setAllocationRowError(row.id, 'targetId', 'Bitte Ziel auswählen.');
                    throw new Error('Bitte Ziel fuer jede Zuordnung auswählen.');
                }

                prepared.push({
                    allocationType,
                    targetId,
                    amount: allocationType === 'EXPENSE' ? -amount : amount,
                    currency,
                    paymentDate: transactionDate,
                });
            }

            await allocatePaymentProviderTransaction(activeTransaction.id, {
                allocations: prepared,
            });
            await fetchTransactions();
            closeAssignDialog();
        } catch (err) {
            const message = err instanceof Error && err.message ? err.message : 'Zuordnung fehlgeschlagen.';
            setAssignError(message);
        } finally {
            setAssigning(false);
        }
    };

    const handleBankAssign = async () => {
        if (!activeBankTransaction) return;
        setBankAssignError('');
        setBankAssigning(true);
        try {
            const prepared = [];
            for (const row of bankAllocationRows) {
                if (!row.allocationType) {
                    setBankAllocationRowError(row.id, 'allocationType', 'Bitte Ziel auswählen.');
                    throw new Error('Bitte Ziel fuer jede Zuordnung auswählen.');
                }
                const amount = parseAmount(row.amount);
                if (amount == null) {
                    setBankAllocationRowError(row.id, 'amount', 'Bitte Betrag eingeben.');
                    throw new Error('Bitte Betrag fuer jede Zuordnung eingeben.');
                }

                let targetId = row.targetId;
                let allocationType = row.allocationType;
                const transactionDate = row.paymentDate || toDateInputValue(activeBankTransaction.bookingDate || activeBankTransaction.valueDate);
                const currency = activeBankTransaction.currency || 'EUR';

                if (allocationType === 'NEW_INCOME') {
                    const payload = buildIncomePayload(row.newIncome, activeBankTransaction, amount, transactionDate);
                    if (!payload.customerName) {
                        setBankAllocationRowError(row.id, 'customerName', 'Bitte Kunde auswählen.');
                        throw new Error('Bitte Kunde auswaehlen.');
                    }
                    const res = await createIncome(payload);
                    targetId = res?.data?.id;
                    allocationType = 'INCOME';
                }

                if (allocationType === 'NEW_EXPENSE') {
                    const payload = buildExpensePayload(row.newExpense, activeBankTransaction, amount, transactionDate, 'Bank');
                    if (!payload.supplierName) {
                        setBankAllocationRowError(row.id, 'supplierName', 'Bitte Lieferant auswählen.');
                        throw new Error('Bitte Lieferant auswaehlen.');
                    }
                    const res = await createExpense(payload);
                    targetId = res?.data?.id;
                    allocationType = 'EXPENSE';
                }

                if (!targetId) {
                    setBankAllocationRowError(row.id, 'targetId', 'Bitte Ziel auswählen.');
                    throw new Error('Bitte Ziel fuer jede Zuordnung auswählen.');
                }

                let amountToSend = amount;
                if (allocationType === 'EXPENSE') {
                    amountToSend = -amountToSend;
                }

                prepared.push({
                    allocationType,
                    targetId,
                    amount: amountToSend,
                    currency,
                    paymentDate: row.paymentDate || transactionDate,
                });
            }

            await allocateBankTransaction(activeBankTransaction.id, {
                allocations: prepared,
                comment: bankComment || undefined,
            });
            await fetchTransactions();
            closeBankAssignDialog();
        } catch (err) {
            const message = err instanceof Error && err.message ? err.message : 'Zuordnung fehlgeschlagen.';
            setBankAssignError(message);
        } finally {
            setBankAssigning(false);
        }
    };

    const handleDeleteTransaction = async (transactionId) => {
        if (!transactionId || deletingTransactionId) {
            return;
        }
        setError('');
        setDeletingTransactionId(transactionId);
        try {
            const res = await deletePaymentProviderTransaction(transactionId);
            if (res.status !== 204) {
                throw new Error('Delete failed');
            }
            setTransactions((prev) => prev.filter((tx) => tx.id !== transactionId));
            if (activeTransaction?.id === transactionId) {
                closeAssignDialog();
            }
        } catch (err) {
            setError('Vorgang konnte nicht gelöscht werden.');
        } finally {
            setDeletingTransactionId(null);
        }
    };

    const handleDeleteBankTransaction = async (transactionId) => {
        if (!transactionId || deletingBankTransactionId) {
            return;
        }
        setBankError('');
        setDeletingBankTransactionId(transactionId);
        try {
            const res = await deleteBankTransaction(transactionId);
            if (res.status !== 204) {
                throw new Error('Delete failed');
            }
            setBankTransactions((prev) => prev.filter((tx) => tx.id !== transactionId));
            if (activeBankTransaction?.id === transactionId) {
                closeBankAssignDialog();
            }
        } catch (err) {
            setBankError('Bankvorgang konnte nicht gelöscht werden.');
        } finally {
            setDeletingBankTransactionId(null);
        }
    };

    return {
        hasAccounting,
        transactions,
        loading,
        error,
        bankTransactions,
        bankLoading,
        bankError,
        bankAssignDialogOpen,
        activeBankTransaction,
        bankAssigning,
        bankAssignError,
        bankAllocationRows,
        bankAllocationErrors,
        bankCreatingCustomer,
        bankCreatingSupplier,
        bankComment,
        setBankComment,
        assignDialogOpen,
        activeTransaction,
        allocationRows,
        assigning,
        deletingTransactionId,
        deletingBankTransactionId,
        assignError,
        allocationErrors,
        referenceLoading,
        referenceData,
        creatingCustomer,
        creatingSupplier,
        fetchTransactions,
        openAssignDialog,
        openBankAssignDialog,
        closeAssignDialog,
        closeBankAssignDialog,
        updateAllocationRow,
        updateAllocationNested,
        updateBankAllocationRow,
        updateBankAllocationNested,
        addAllocationRow,
        removeAllocationRow,
        addBankAllocationRow,
        removeBankAllocationRow,
        handleAllocationTypeChange,
        handleBankAllocationTypeChange,
        handleCustomerSelection,
        handleCustomerInput,
        handleSupplierSelection,
        handleSupplierInput,
        handleCreateCustomer,
        handleCreateSupplier,
        handleBankCreateCustomer,
        handleBankCreateSupplier,
        handleAssign,
        handleBankAssign,
        handleDeleteTransaction,
        handleDeleteBankTransaction,
        setAllocationRowError,
        setBankAllocationRowError,
        loadReferenceData,
        customerOptions: buildCustomerOptions(referenceData.customers),
        supplierOptions: buildSupplierOptions(referenceData.suppliers),
    };
}
